import { z } from "zod";
import {
  Box,
  CategoricalBarChart,
  WidgetEmpty,
  WidgetSkeleton,
  truncateLabel,
  useCategoricalColors,
  useWidgetInstance,
  type WidgetModule,
  type WidgetRenderContext,
  type WidgetSettingsGroup,
} from "@salestrak/ui";
import type { WidgetTag } from "#app/widgets/dashboards/widget-tags";
import type { WidgetSection } from "#app/widgets/dashboards/widget-sections";
import { widgetFiltersSchema } from "#app/widgets/dashboards/widget-filters";
import { useWidgetDrilldown } from "#app/widgets/dashboards/widget-drilldown";
import { useWindowEmptyTitle } from "#app/widgets/dashboards/scope/use-window-copy";
import { WidgetHeaderFilters } from "../shared/widget-header-filters";
import { WidgetChartTooltip } from "../shared/widget-chart-tooltip";
import { resolveStoredTitle } from "../shared/derived-titles";
import { useStageAge, type StageAgeRow } from "./use-stage-age";

/**
 * `stage-age` — a themed bar chart of how long projects dwell in each stage
 * (average or maximum days), on the `CategoricalBarChart` finished chart.
 * Answers the Add-card "Total time in stage / Age" bar. Dwell is real
 * time-in-stage from the server's `issueStageDwell` aggregation over
 * timestamped stage-transition activities. Each bar wears its stage's OWN
 * color (`useCategoricalColors`); pressing one opens this card's Data drawer
 * on that stage's projects (`dimension: "stage"`).
 */
export const stageAgeConfigSchema = z
  .looseObject({
    measure: z.enum(["avg", "max"]).default("avg"),
    sort: z.enum(["duration", "alpha"]).default("duration"),
    showGrid: z.boolean().default(true),
    showAxes: z.boolean().default(true),
    filters: widgetFiltersSchema.nullish(),
  });

export type StageAgeConfig = z.infer<typeof stageAgeConfigSchema>;

/** Title mechanics: the per-measure derived titles plus the generic name are
 *  widget-generated and re-derive when the measure flips; renames stick. */
const AGE_MEASURE_TITLE: Record<StageAgeConfig["measure"], string> = {
  avg: "Average time in stage",
  max: "Max time in stage",
};
const GENERATED_AGE_TITLES = new Set(["Time in stage", ...Object.values(AGE_MEASURE_TITLE)]);

function StageAgeRender({ h, config }: WidgetRenderContext<StageAgeConfig>) {
  const { data, loading, error } = useStageAge(config.measure, config.sort, config.filters);
  const sliceColors = useCategoricalColors(data);
  // Click-through: a bar press opens this card's Data drawer on the stage's rows.
  const drill = useWidgetDrilldown();
  const instance = useWidgetInstance();
  const emptyTitle = useWindowEmptyTitle("No Projects", config.filters);

  if (error) throw error;
  if (loading) {
    return <WidgetSkeleton variant="columns" />;
  }
  if (data.length === 0) {
    return (
      <WidgetEmpty title={emptyTitle} />
    );
  }

  const compact = h < 3;
  const showAxes = config.showAxes && !compact;
  const showGrid = config.showGrid && !compact;

  const handleBarClick =
    drill && instance
      ? (row: StageAgeRow) => {
          drill.openData(instance.instanceId, {
            kind: "dimension",
            dimension: "stage",
            ids: [row.id],
            label: row.label,
          });
        }
      : undefined;

  return (
    <Box direction="column" fill="flex" gap="xs">
      <Box fill="flex">
        <CategoricalBarChart<StageAgeRow>
          data={data}
          categoryKey="label"
          series={[{ key: "days" }]}
          categoryColorTokens={sliceColors.tokens}
          showGrid={showGrid}
          showAxes={showAxes}
          // The value axis drops out on a short card, but the category axis is
          // what NAMES each bar — without it the card is anonymous stripes. Only
          // the user's own Axes toggle turns it off.
          showCategoryAxis={config.showAxes}
          angledCategoryTicks
          categoryTickFormatter={(v) => truncateLabel(v)}
          valueTickFormatter={(v) => `${v}d`}
          onBarClick={handleBarClick}
          ariaLabel="Project age by stage"
          renderTooltip={(row) => (
            <WidgetChartTooltip
              label={row.label}
              details={`${row.days} ${row.days === 1 ? "day" : "days"} ${config.measure === "max" ? "oldest" : "avg"} · ${row.count.toLocaleString()} ${row.count === 1 ? "Project" : "Projects"}`}
            />
          )}
        />
      </Box>
    </Box>
  );
}

const stageAgeSettings: WidgetSettingsGroup[] = [
  {
    title: "Data",
    fields: [
      {
        key: "measure",
        kind: "select",
        label: "Measure",
        icon: "clock",
        options: [
          { value: "avg", label: "Average age" },
          { value: "max", label: "Oldest Project" },
        ],
      },
      {
        key: "sort",
        kind: "select",
        label: "Sort by",
        icon: "arrow-down-wide-narrow",
        options: [
          { value: "duration", label: "Longest" },
          { value: "alpha", label: "Alphabetical" },
        ],
      },
    ],
  },
  {
    title: "Display",
    fields: [
      { key: "showGrid", kind: "toggle", label: "Gridlines", icon: "grid-dots" },
      { key: "showAxes", kind: "toggle", label: "Axes", icon: "ruler" },
    ],
  },
  {
    title: "Filters",
    fields: [{ key: "filters", kind: "issueFilters", label: "Filters" }],
  },
];

export const stageAgeDef: WidgetModule<StageAgeConfig> = {
  type: "stage-age",
  title: "Time in stage",
  dynamicTitle: (config) =>
    resolveStoredTitle(
      config,
      (title) => GENERATED_AGE_TITLES.has(title),
      AGE_MEASURE_TITLE[config.measure],
    ),
  icon: "clock",
  span: { minW: 3, minH: 3, maxW: 12, maxH: 12, defaultW: 5 },
  configSchema: stageAgeConfigSchema,
  settings: stageAgeSettings,
  headerActions: WidgetHeaderFilters,
  // Chart body: grab the card from anywhere on it — wedge/bar/point clicks
  // still land because drag activation needs 4px of movement.
  bodyDraggable: true,
  catalog: [
    {
      key: "stage-age.stage",
      label: "Time in stage",
      description: "How long Projects have been sitting in each workflow stage, as bars.",
      section: "stages" satisfies WidgetSection,
      tags: ["projects", "stages", "ageHealth"] satisfies readonly WidgetTag[],
      // Label is in the generated family — bake nothing, let `dynamicTitle` track.
      autoTitle: false,
      defaultConfig: { measure: "avg" },
      defaultW: 5,
    },
  ],
  render: StageAgeRender,
};
